import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  downloadSekolahTemplate, 
  downloadUsersTemplate, 
  bulkImportSekolah, 
  bulkImportUsers 
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Upload, Download, FileSpreadsheet, Building2, Users, 
  CheckCircle, XCircle, AlertCircle, Loader2, FileDown, Info
} from 'lucide-react';
import { toast } from '../ui/sonner';

const BulkImportPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('sekolah');
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  
  const fileInputRef = useRef(null);

  // Check permission
  if (user?.role !== 'admin_dinas') {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <Alert variant="destructive">
          <AlertCircle className="w-5 h-5" />
          <AlertDescription>
            Hanya Admin Dinas yang dapat mengakses halaman ini.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Download template
  const handleDownloadTemplate = async (type) => {
    setDownloadingTemplate(true);
    try {
      const blob = type === 'sekolah' 
        ? await downloadSekolahTemplate()
        : await downloadUsersTemplate();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'sekolah' 
        ? 'template_import_sekolah.xlsx' 
        : 'template_import_pengguna.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template berhasil didownload');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Gagal mendownload template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('File harus berformat Excel (.xlsx atau .xls)');
        return;
      }
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Pilih file terlebih dahulu');
      return;
    }

    setUploading(true);
    setImportResults(null);

    try {
      const results = activeTab === 'sekolah'
        ? await bulkImportSekolah(selectedFile)
        : await bulkImportUsers(selectedFile);
      
      setImportResults(results);
      
      if (results.success > 0) {
        toast.success(`${results.success} data berhasil diimport`);
      }
      if (results.failed > 0) {
        toast.warning(`${results.failed} data gagal diimport`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Gagal melakukan import');
    } finally {
      setUploading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="bulk-import-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">Bulk Import Data</h1>
        <p className="text-gray-500">Import data sekolah dan pengguna dari file Excel</p>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6 border-blue-200 bg-blue-50">
        <Info className="w-5 h-5 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Petunjuk:</strong> Download template terlebih dahulu, isi data sesuai format, 
          lalu upload file untuk melakukan import. Pastikan data yang diisi sesuai dengan format yang diminta.
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetForm(); }}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="sekolah" className="flex items-center gap-2" data-testid="tab-sekolah">
            <Building2 className="w-4 h-4" />
            Import Sekolah
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            Import Pengguna
          </TabsTrigger>
        </TabsList>

        {/* Sekolah Import Tab */}
        <TabsContent value="sekolah">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Template Download Card */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileDown className="w-5 h-5 text-indigo-600" />
                  Download Template
                </CardTitle>
                <CardDescription>
                  Download template Excel untuk import data sekolah
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-3">Kolom yang tersedia:</p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• <strong>Nama Sekolah *</strong> - Wajib diisi</li>
                      <li>• <strong>NPSN</strong> - Nomor Pokok Sekolah Nasional</li>
                      <li>• <strong>Jenjang *</strong> - KB, TK-PAUD, SD, SMP, SMA/Sederajat, SMK</li>
                      <li>• <strong>Alamat *</strong> - Wajib diisi</li>
                      <li>• <strong>Kampung/Desa</strong> - Opsional</li>
                      <li>• <strong>Distrik</strong> - Opsional</li>
                      <li>• <strong>Latitude *</strong> - Wajib diisi</li>
                      <li>• <strong>Longitude *</strong> - Wajib diisi</li>
                      <li>• <strong>Radius Geofence</strong> - Default: 100 meter</li>
                      <li>• <strong>Jam Masuk</strong> - Default: 07:00</li>
                      <li>• <strong>Jam Pulang</strong> - Default: 14:00</li>
                    </ul>
                  </div>
                  <Button 
                    onClick={() => handleDownloadTemplate('sekolah')}
                    disabled={downloadingTemplate}
                    className="w-full bg-indigo-950 hover:bg-indigo-900"
                    data-testid="download-sekolah-template"
                  >
                    {downloadingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Template Sekolah
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upload Card */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Upload className="w-5 h-5 text-emerald-600" />
                  Upload File
                </CardTitle>
                <CardDescription>
                  Upload file Excel yang sudah diisi
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* File Input */}
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input-sekolah"
                    />
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    {selectedFile ? (
                      <div>
                        <p className="font-medium text-indigo-950">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-600">Klik atau drag file ke sini</p>
                        <p className="text-sm text-gray-400">Format: .xlsx atau .xls</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetForm}
                      disabled={!selectedFile || uploading}
                      className="flex-1"
                    >
                      Reset
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={!selectedFile || uploading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="import-sekolah-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Import Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Import Tab */}
        <TabsContent value="users">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Template Download Card */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileDown className="w-5 h-5 text-indigo-600" />
                  Download Template
                </CardTitle>
                <CardDescription>
                  Download template Excel untuk import data pengguna
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-3">Kolom yang tersedia:</p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• <strong>NIP/NUPTK *</strong> - Wajib diisi, harus unik</li>
                      <li>• <strong>Nama Lengkap *</strong> - Wajib diisi</li>
                      <li>• <strong>Email</strong> - Opsional</li>
                      <li>• <strong>Role *</strong> - kepala_sekolah, guru, tenaga_tata_usaha</li>
                      <li>• <strong>Status Kepegawaian</strong> - ASN, PPPK, Guru Kontrak, dll</li>
                      <li>• <strong>Nama Sekolah</strong> - Harus sesuai dengan sekolah terdaftar</li>
                      <li>• <strong>Password *</strong> - Min 8 karakter, huruf besar/kecil, angka</li>
                    </ul>
                  </div>
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-xs">
                      Pastikan sekolah sudah terdaftar sebelum import pengguna. 
                      Nama sekolah harus persis sama dengan yang ada di sistem.
                    </AlertDescription>
                  </Alert>
                  <Button 
                    onClick={() => handleDownloadTemplate('users')}
                    disabled={downloadingTemplate}
                    className="w-full bg-indigo-950 hover:bg-indigo-900"
                    data-testid="download-users-template"
                  >
                    {downloadingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Template Pengguna
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upload Card */}
            <Card className="border-indigo-100/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Upload className="w-5 h-5 text-emerald-600" />
                  Upload File
                </CardTitle>
                <CardDescription>
                  Upload file Excel yang sudah diisi
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* File Input */}
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input-users"
                    />
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    {selectedFile ? (
                      <div>
                        <p className="font-medium text-indigo-950">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-600">Klik atau drag file ke sini</p>
                        <p className="text-sm text-gray-400">Format: .xlsx atau .xls</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetForm}
                      disabled={!selectedFile || uploading}
                      className="flex-1"
                    >
                      Reset
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={!selectedFile || uploading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="import-users-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Import Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Import Results */}
      {importResults && (
        <Card className="mt-6 border-indigo-100/50" data-testid="import-results">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Hasil Import
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-indigo-950">{importResults.total}</p>
                <p className="text-sm text-gray-500">Total Data</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600">{importResults.success}</p>
                <p className="text-sm text-emerald-700">Berhasil</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                <p className="text-sm text-red-700">Gagal</p>
              </div>
            </div>

            {/* Success List */}
            {importResults.success > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-emerald-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Data Berhasil Diimport ({importResults.success})
                </h4>
                <div className="max-h-40 overflow-y-auto bg-emerald-50 rounded-lg p-3">
                  {activeTab === 'sekolah' ? (
                    importResults.created_schools?.map((school, idx) => (
                      <div key={idx} className="text-sm py-1 border-b border-emerald-100 last:border-0">
                        <span className="font-medium">{school.nama}</span>
                      </div>
                    ))
                  ) : (
                    importResults.created_users?.map((user, idx) => (
                      <div key={idx} className="text-sm py-1 border-b border-emerald-100 last:border-0 flex justify-between">
                        <span className="font-medium">{user.nama}</span>
                        <span className="text-emerald-600">{user.role}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Error List */}
            {importResults.errors?.length > 0 && (
              <div>
                <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Error ({importResults.errors.length})
                </h4>
                <div className="max-h-60 overflow-y-auto bg-red-50 rounded-lg p-3">
                  {importResults.errors.map((error, idx) => (
                    <div key={idx} className="text-sm text-red-700 py-1 border-b border-red-100 last:border-0">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImportPage;
